import { Link } from 'react-router-dom'
import { AdminProfile } from '@/hooks/useAdminProfiles'

interface ProfileCardProps {
  profile: AdminProfile
  selected?: boolean
  onSelect?: (selected: boolean) => void
}

export default function ProfileCard({ profile, selected = false, onSelect }: ProfileCardProps) {
  return (
    <div
      className={`bg-[#141c24] border rounded-sm p-4 hover:border-[#FFB84D]/30 transition-all ${
        selected ? 'border-[#FFB84D]' : 'border-[#2a3542]'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        {onSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            className="mt-1"
          />
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <Link
                to={`/employee/mindmetal/profiles/${profile.profile_id}`}
                className="text-[#e8eaed] font-semibold hover:text-[#FFB84D] transition-colors"
              >
                {profile.canonical_id}
              </Link>
              <div className="text-xs text-[#8b949e] font-mono mt-1">
                ID: {profile.profile_id}
              </div>
            </div>

            {/* Status Badges */}
            <div className="flex items-center gap-2">
              <CoverageBadge level={profile.coverage_level} />
              <StabilityBadge indicator={profile.stability_indicator} />
              {!profile.is_active && (
                <span className="px-2 py-0.5 rounded text-xs font-mono bg-gray-500/10 text-gray-400 border border-gray-500/30">
                  INACTIVE
                </span>
              )}
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {profile.tenant_name && (
              <div>
                <span className="text-[#8b949e]">Tenant:</span>{' '}
                <span className="text-[#e8eaed]">{profile.tenant_name}</span>
              </div>
            )}
            {profile.roscoe_user_id && (
              <div>
                <span className="text-[#8b949e]">Roscoe ID:</span>{' '}
                <span className="text-[#e8eaed] font-mono">{profile.roscoe_user_id}</span>
              </div>
            )}
            <div>
              <span className="text-[#8b949e]">Observations:</span>{' '}
              <span className="text-[#e8eaed]">{profile.total_observation_count}</span>
            </div>
            <div>
              <span className="text-[#8b949e]">Created:</span>{' '}
              <span className="text-[#e8eaed]">
                {new Date(profile.created_at).toLocaleDateString()}
              </span>
            </div>
            {profile.last_distillation && (
              <div className="col-span-2">
                <span className="text-[#8b949e]">Last Distillation:</span>{' '}
                <span className="text-[#e8eaed]">
                  {new Date(profile.last_distillation).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function CoverageBadge({ level }: { level: string }) {
  const styles: Record<string, string> = {
    minimal: 'bg-red-500/10 text-red-400 border-red-500/30',
    emerging: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    established: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    mature: 'bg-green-500/10 text-green-400 border-green-500/30',
  }

  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-mono border uppercase ${
        styles[level] || styles.minimal
      }`}
    >
      {level}
    </span>
  )
}

function StabilityBadge({ indicator }: { indicator: string }) {
  const styles: Record<string, string> = {
    volatile: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    stabilizing: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    stable: 'bg-green-500/10 text-green-400 border-green-500/30',
  }

  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-mono border uppercase ${
        styles[indicator] || styles.volatile
      }`}
    >
      {indicator}
    </span>
  )
}
